import type { Batch, ResumeBlocker } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

/**
 * **멈춤 한 줄**(정본 `07` §4.12 · 신설 2026-10-09 · 서버 v0.9.4) — 멈춘 배치가 «그냥 멈춘 것»인지 «문제»인지를 가른다.
 * eric 원문(계약 `Batch.resume_blockers` 설명에 박혀 있다): 「표시가 없으니까 멈춘건지 문제인건지 알 수가 없다」.
 *
 * | `halt_resume` | `resume_blockers` | 한 줄 | 목록 |
 * | --- | --- | --- | --- |
 * | `auto` | 무엇이든 | 「자동으로 이어 갑니다」 | 막는 것이 있으면 그 목록 |
 * | `human` | **빈 배열** | **그리지 않는다** — [재개]만 강조(`cleared`) | — |
 * | `human` | 1줄 이상 | 「조치가 필요합니다」 | 서버 문장 그대로 |
 * | `null` | — | 없다(안 멈췄다) | — |
 *
 * 🔴 **판정은 서버 칸이다 — 화면은 고르기만 한다.** 상태(`status`) · 까닭(`halt_reason`) · 막는 문장의 글자로 갈래를 정하지 않는다.
 * 🔴 **빈 배열은 「모름」이 아니다** — 계약이 「빈 배열 = 막는 것이 없다 · [재개]만 누르면 이어 간다」로 못 박았다.
 *    `[]` 를 `null` 과 같게 접으면 eric 이 짚은 바로 그 상태(멈춘 건지 문제인지 모름)로 돌아간다.
 * ⚠ 거꾸로 **`null` 을 빈 배열로 접지도 않는다.** `human` 인데 `resume_blockers` 가 `null` 이면(계약상 `paused`·`error` 가
 *   아닐 때) 막는 것이 없는지 «모른다» — 「오류가 풀렸습니다」를 지어 세우지 않고 한 줄을 안 그린다.
 * 🔴 **문장은 서버 것이다** — 고치거나 줄이거나 번역하지 않는다(정본 「문장 목록 그대로」).
 * 🔴 **버튼 활성은 여기서 정하지 않는다** — 여전히 `allowed_commands` 하나다. `cleared` 는 [재개]를 «강조»할 뿐이다.
 * 🔴 **`cleared` 는 한 줄을 그리지 않는다**(eric 2026-10-14 「일시 정지를 눌렀는데.. 이런 텍스트가.. 무슨 뜻이야?」 →
 *    「이거 필요없지 않아?」). 정본 원문 「오류가 풀렸습니다 — [재개]를 누르면…」은 사람이 일시정지해도 같은 칸(`human` + 빈 배열)이
 *    와서 없는 오류를 지어냈고, 막는 것이 없다는 말은 상태 배지(일시정지) · 주 버튼([재개])이 이미 한다. 갈래는 남긴다 —
 *    작업 줄이 [재개]를 주 버튼으로 세우는 데 쓴다. 그리는 것은 `auto` · `action` · `confirm` 셋(`shownHaltLine`)이다.
 *
 * ## 서버 머리 한 줄(v0.9.5)
 * `resume_headline`이 있으면 kind/text 그대로 표시한다(act → action, ready → cleared).
 * ready는 계약상 사람 재개 ∧ 막음 0이므로 위 cleared 숨김 결정을 유지한다. 없으면 아래 옛 서버 판정을 쓴다.
 *
 * ## 확인 재개(`resume_route=confirm` · 백엔드 0.28.15 · eric 2026-10-22)
 *
 * 서버가 «어떤 단추로 잇는가»(`Batch.resume_route`)를 직접 준다. **이 칸이 먼저다** — `auto` 면 자동, `confirm` 이면
 * 「현장 확인이 필요합니다 — [복구] «멈춘 동작부터 다시»」 + 확인할 것(`resume_checks`) + 막는 문장, `resume` 이면 위 표대로
 * `resume_blockers` 로 가른다. 칸이 없으면(옛 서버) 위 표 그대로다(`halt_resume`).
 */
export type HaltLine =
  | { kind: 'auto'; blockers: readonly ResumeBlocker[]; text?: string }
  | { kind: 'cleared' }
  | { kind: 'action'; blockers: readonly ResumeBlocker[]; text?: string }
  | {
      kind: 'confirm'
      blockers: readonly ResumeBlocker[]
      checks: readonly string[]
      text?: string
    }

export function haltLine(
  batch:
    | Pick<
        Batch,
        'halt_resume' | 'resume_blockers' | 'resume_route' | 'resume_checks' | 'resume_headline'
      >
    | undefined,
): HaltLine | undefined {
  if (batch === undefined) return undefined
  const blockers = batch.resume_blockers
  const headline = batch.resume_headline
  if (headline != null) {
    // ready 자체가 서버의 사람 재개 ∧ 막음 0 판정이다. 기존 cleared 숨김/재개 강조를 유지한다.
    if (headline.kind === 'ready') return { kind: 'cleared' }
    if (headline.kind === 'confirm')
      return {
        kind: 'confirm',
        text: headline.text,
        blockers: blockers ?? [],
        checks: batch.resume_checks ?? [],
      }
    return {
      kind: headline.kind === 'act' ? 'action' : 'auto',
      text: headline.text,
      blockers: blockers ?? [],
    }
  }
  // 🔴 서버의 «잇는 단추» 칸이 먼저다(머리말) — 문장으로 가르지 않는다
  if (batch.resume_route === 'confirm')
    return { kind: 'confirm', blockers: blockers ?? [], checks: batch.resume_checks ?? [] }
  const route = batch.resume_route ?? (batch.halt_resume === 'human' ? 'resume' : batch.halt_resume)
  if (route === 'auto') return { kind: 'auto', blockers: blockers ?? [] }
  if (route !== 'resume') return undefined
  /* 모른다 — 「풀렸다」로도 「조치 필요」로도 접지 않는다(머리말) */
  if (blockers == null) return undefined
  return blockers.length === 0 ? { kind: 'cleared' } : { kind: 'action', blockers }
}

/** 화면에 한 줄로 서는 갈래 — `cleared` 는 빠진다(머리말) */
export type ShownHaltLine = Exclude<HaltLine, { kind: 'cleared' }>

/** 그릴 한 줄 — `cleared` · 없음이면 `undefined` */
export function shownHaltLine(line: HaltLine | undefined): ShownHaltLine | undefined {
  return line === undefined || line.kind === 'cleared' ? undefined : line
}

/** 갈래마다 글리프 — 🔴 색만으로 가르지 않는다(글자가 이미 다르고, 글리프가 한 번 더 말한다) */
export const HALT_GLYPH: Record<ShownHaltLine['kind'], string> = {
  auto: '↻',
  action: '▲',
  confirm: '▲',
}

const HALT_TEXT_KEY = {
  auto: 'halt.auto',
  action: 'halt.action',
  confirm: 'halt.confirm',
} as const

/** 한 줄 글자(정본 원문) — 작업 줄과 작업 상세가 같은 표를 쓴다 */
export function haltLineText(line: ShownHaltLine): string {
  return line.text ?? t(HALT_TEXT_KEY[line.kind])
}
